import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, ArrowLeft, Inbox, Phone, Mail, MessageSquare, KeyRound, Bot, UserRound } from "lucide-react";
import { Logo } from "../components/Logo";
import { useAuth, formatApiErrorDetail } from "../auth/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const { user, logout } = useAuth();
  const [subs, setSubs] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("subs");
  const [chats, setChats] = useState(null);
  const [chatsError, setChatsError] = useState("");
  const [leads, setLeads] = useState(null);
  const [leadsError, setLeadsError] = useState("");
  const [openChat, setOpenChat] = useState(null);
  const [transcript, setTranscript] = useState(null);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwMsg, setPwMsg] = useState(null);
  const [pwLoading, setPwLoading] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/submissions`, { withCredentials: true })
      .then((r) => setSubs(r.data))
      .catch(() => setError("Aanvragen laden mislukt."));
  }, []);

  useEffect(() => {
    if (tab !== "chats" || chats !== null) return;
    axios
      .get(`${API}/admin/chats`, { withCredentials: true })
      .then((r) => setChats(r.data))
      .catch(() => setChatsError("AI-gesprekken laden mislukt."));
  }, [tab, chats]);

  useEffect(() => {
    if (tab !== "leads" || leads !== null) return;
    axios
      .get(`${API}/admin/leads`, { withCredentials: true })
      .then((r) => setLeads(r.data))
      .catch(() => setLeadsError("Leads laden mislukt."));
  }, [tab, leads]);

  const toggleChat = (sessionId) => {
    if (openChat === sessionId) {
      setOpenChat(null);
      setTranscript(null);
      return;
    }
    setOpenChat(sessionId);
    setTranscript(null);
    axios
      .get(`${API}/admin/chats/${sessionId}`, { withCredentials: true })
      .then((r) => setTranscript(r.data))
      .catch(() => setTranscript([]));
  };

  const submitPw = async (e) => {
    e.preventDefault();
    if (pw.next.length < 8) return setPwMsg({ type: "err", text: "Nieuw wachtwoord moet minimaal 8 tekens zijn." });
    if (pw.next !== pw.confirm) return setPwMsg({ type: "err", text: "Nieuwe wachtwoorden komen niet overeen." });
    setPwLoading(true);
    setPwMsg(null);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { current_password: pw.current, new_password: pw.next },
        { withCredentials: true }
      );
      setPwMsg({ type: "ok", text: "Wachtwoord succesvol aangepast." });
      setPw({ current: "", next: "", confirm: "" });
    } catch (err) {
      setPwMsg({ type: "err", text: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setPwLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink" data-testid="admin-page">
      <header className="border-b border-white/10 bg-[#222222]">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Logo />
          <div className="flex items-center gap-4">
            <span className="hidden font-mono text-xs text-white/50 sm:block" data-testid="admin-user-email">{user?.email}</span>
            <button
              onClick={logout}
              className="flex items-center gap-2 rounded-[10px] border border-white/20 px-4 py-2 font-archivo text-sm font-bold text-white transition-colors hover:border-amber/60"
              data-testid="admin-logout-button"
            >
              <LogOut size={15} />
              Uitloggen
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-archivo text-3xl font-black tracking-tight text-cream" data-testid="admin-heading">Admin-paneel</h1>
            <div className="mt-4 inline-flex rounded-[10px] border border-edge/70 bg-white p-1" data-testid="admin-tabs">
              <button
                onClick={() => setTab("subs")}
                className={`rounded-[8px] px-4 py-2 font-archivo text-sm font-bold transition-colors ${tab === "subs" ? "bg-amber text-[#181510]" : "text-faint hover:text-cream"}`}
                data-testid="admin-tab-submissions"
              >
                Aanvragen
              </button>
              <button
                onClick={() => setTab("chats")}
                className={`rounded-[8px] px-4 py-2 font-archivo text-sm font-bold transition-colors ${tab === "chats" ? "bg-amber text-[#181510]" : "text-faint hover:text-cream"}`}
                data-testid="admin-tab-chats"
              >
                AI-gesprekken
              </button>
              <button
                onClick={() => setTab("leads")}
                className={`rounded-[8px] px-4 py-2 font-archivo text-sm font-bold transition-colors ${tab === "leads" ? "bg-amber text-[#181510]" : "text-faint hover:text-cream"}`}
                data-testid="admin-tab-leads"
              >
                Leads
              </button>
            </div>
          </div>
          <Link to="/" className="inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.2em] text-faint transition-colors hover:text-amberdeep" data-testid="admin-back-link">
            <ArrowLeft size={13} />
            Terug naar de site
          </Link>
        </div>

        {tab === "subs" && (<>
        <p className="mt-4 text-sm text-faint" data-testid="admin-subs-count">
          {subs ? `${subs.length} aanvraag${subs.length === 1 ? "" : "en"}` : "Laden…"}
        </p>

        {error && <p className="mt-6 text-sm text-red-600" data-testid="admin-error">{error}</p>}

        {subs && subs.length === 0 && (
          <div className="mt-10 flex flex-col items-center gap-3 rounded-[12px] border border-edge/60 bg-white p-12 text-center" data-testid="admin-empty">
            <Inbox size={28} className="text-faint" />
            <p className="text-sm text-faint">Nog geen aanvragen. Nieuwe formulierinzendingen verschijnen hier.</p>
          </div>
        )}

        <div className="mt-8 space-y-4">
          {subs?.map((s, i) => (
            <motion.div
              key={s.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className="rounded-[12px] border border-edge/60 bg-white p-6"
              data-testid={`submission-card-${i}`}
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-archivo text-lg font-extrabold text-cream" data-testid={`submission-name-${i}`}>{s.naam}</p>
                <p className="font-mono text-[11px] text-faint" data-testid={`submission-date-${i}`}>
                  {new Date(s.created_at).toLocaleString("nl-NL", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                </p>
              </div>
              <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1.5 text-sm">
                <a href={`tel:${s.telefoon}`} className="flex items-center gap-1.5 text-cream transition-colors hover:text-amberdeep" data-testid={`submission-phone-${i}`}>
                  <Phone size={14} className="text-amberdeep" />
                  {s.telefoon}
                </a>
                <a href={`mailto:${s.email}`} className="flex items-center gap-1.5 text-cream transition-colors hover:text-amberdeep" data-testid={`submission-email-${i}`}>
                  <Mail size={14} className="text-amberdeep" />
                  {s.email}
                </a>
              </div>
              <div className="mt-4 flex gap-2.5 rounded-[10px] bg-[#faf7ef] p-4">
                <MessageSquare size={15} className="mt-0.5 shrink-0 text-amberdeep" />
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-cream" data-testid={`submission-message-${i}`}>{s.bericht}</p>
              </div>
            </motion.div>
          ))}
        </div>

        </>)}

        {tab === "chats" && (
          <>
            {chatsError && <p className="mt-6 text-sm text-red-600" data-testid="admin-chats-error">{chatsError}</p>}
            {chats && chats.length === 0 && (
              <div className="mt-10 flex flex-col items-center gap-3 rounded-[12px] border border-edge/60 bg-white p-12 text-center" data-testid="admin-chats-empty">
                <Bot size={28} className="text-faint" />
                <p className="text-sm text-faint">Nog geen AI-gesprekken. Nieuwe chats van bezoekers verschijnen hier.</p>
              </div>
            )}
            <div className="mt-8 space-y-4">
              {chats?.map((c, i) => (
                <div key={c.session_id} className="rounded-[12px] border border-edge/60 bg-white p-6" data-testid={`chat-session-card-${i}`}>
                  <button onClick={() => toggleChat(c.session_id)} className="flex w-full flex-wrap items-center justify-between gap-2 text-left" data-testid={`chat-session-toggle-${i}`}>
                    <div className="flex items-center gap-3">
                      <Bot size={17} className="shrink-0 text-amberdeep" />
                      <div>
                        <p className="font-archivo text-sm font-extrabold text-cream" data-testid={`chat-session-preview-${i}`}>{c.preview || "(geen berichten)"}</p>
                        <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.2em] text-faint">{c.messages} berichten</p>
                      </div>
                    </div>
                    <p className="font-mono text-[11px] text-faint" data-testid={`chat-session-date-${i}`}>
                      {new Date(c.last_at).toLocaleString("nl-NL", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </p>
                  </button>
                  {openChat === c.session_id && (
                    <div className="mt-4 space-y-2.5 border-t border-edge/40 pt-4" data-testid={`chat-transcript-${i}`}>
                      {!transcript && <p className="text-sm text-faint">Laden…</p>}
                      {transcript?.map((m, j) => (
                        <div key={j} className={`max-w-[85%] rounded-[10px] p-3 text-sm leading-relaxed ${m.role === "user" ? "ml-auto bg-[#181510] text-white" : "border border-edge/60 bg-[#faf7ef] text-cream"}`} data-testid={`chat-transcript-msg-${j}`}>
                          <p className="whitespace-pre-wrap">{m.content}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {tab === "leads" && (
          <>
            {leadsError && <p className="mt-6 text-sm text-red-600" data-testid="admin-leads-error">{leadsError}</p>}
            {leads && leads.length === 0 && (
              <div className="mt-10 flex flex-col items-center gap-3 rounded-[12px] border border-edge/60 bg-white p-12 text-center" data-testid="admin-leads-empty">
                <UserRound size={28} className="text-faint" />
                <p className="text-sm text-faint">Nog geen leads. Bezoekers die naam en e-mail delen in de chat verschijnen hier.</p>
              </div>
            )}
            <div className="mt-8 space-y-4">
              {leads?.map((l, i) => (
                <div key={l.session_id} className="rounded-[12px] border border-edge/60 bg-white p-6" data-testid={`lead-card-${i}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="font-archivo text-lg font-extrabold text-cream" data-testid={`lead-name-${i}`}>{l.name || "Naam onbekend"}</p>
                    <p className="font-mono text-[11px] text-faint" data-testid={`lead-date-${i}`}>
                      {new Date(l.created_at).toLocaleString("nl-NL", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </p>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-1.5 text-sm">
                    <a href={`mailto:${l.email}`} className="flex items-center gap-1.5 text-cream transition-colors hover:text-amberdeep" data-testid={`lead-email-${i}`}>
                      <Mail size={14} className="text-amberdeep" />
                      {l.email}
                    </a>
                    <button onClick={() => toggleChat(l.session_id)} className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.15em] text-faint transition-colors hover:text-amberdeep" data-testid={`lead-transcript-toggle-${i}`}>
                      <MessageSquare size={13} />
                      {openChat === l.session_id ? "Verberg gesprek" : "Bekijk gesprek"}
                    </button>
                  </div>
                  {openChat === l.session_id && (
                    <div className="mt-4 space-y-2.5 border-t border-edge/40 pt-4" data-testid={`lead-transcript-${i}`}>
                      {!transcript && <p className="text-sm text-faint">Laden…</p>}
                      {transcript?.map((m, j) => (
                        <div key={j} className={`max-w-[85%] rounded-[10px] p-3 text-sm leading-relaxed ${m.role === "user" ? "ml-auto bg-[#181510] text-white" : "border border-edge/60 bg-[#faf7ef] text-cream"}`} data-testid={`lead-transcript-msg-${j}`}>
                          <p className="whitespace-pre-wrap">{m.content}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        <div className="mt-12 max-w-md rounded-[12px] border border-edge/60 bg-white p-6" data-testid="password-change-card">
          <div className="flex items-center gap-2.5">
            <KeyRound size={17} className="text-amberdeep" />
            <h2 className="font-archivo text-lg font-extrabold text-cream">Wachtwoord wijzigen</h2>
          </div>
          <form onSubmit={submitPw} className="mt-4" data-testid="password-change-form">
            <label htmlFor="pw-current" className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-faint">Huidig wachtwoord</label>
            <input id="pw-current" type="password" required value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} className="input-field" data-testid="pw-current-input" />
            <label htmlFor="pw-next" className="mb-1.5 mt-4 block font-mono text-[10px] uppercase tracking-[0.2em] text-faint">Nieuw wachtwoord</label>
            <input id="pw-next" type="password" required minLength={8} value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} className="input-field" data-testid="pw-next-input" />
            <label htmlFor="pw-confirm" className="mb-1.5 mt-4 block font-mono text-[10px] uppercase tracking-[0.2em] text-faint">Bevestig nieuw wachtwoord</label>
            <input id="pw-confirm" type="password" required minLength={8} value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className="input-field" data-testid="pw-confirm-input" />
            {pwMsg && (
              <p
                className={`mt-4 rounded-[8px] border px-3 py-2 text-sm ${
                  pwMsg.type === "ok" ? "border-green-200 bg-green-50 text-green-700" : "border-red-200 bg-red-50 text-red-700"
                }`}
                data-testid="pw-change-message"
              >
                {pwMsg.text}
              </p>
            )}
            <button
              type="submit"
              disabled={pwLoading}
              className="mt-5 w-full rounded-[10px] bg-amber px-6 py-3 font-archivo text-sm font-bold text-[#181510] transition-colors hover:bg-amberdeep disabled:opacity-60"
              data-testid="pw-change-submit"
            >
              {pwLoading ? "Opslaan..." : "Wachtwoord opslaan"}
            </button>
          </form>
        </div>
      </main>
    </div>
  );
}
